import { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import AlertModal from "../../../components/alertModal/alertModal";
import CustomBreadcrumb from "../../../components/customBreadcrumb/CustomBreadcrumb";
import EmptyMessage from "../../../components/emptyMessage/EmptyMessage";
import EmptyPlaceholder from "../../../components/emptyPlaceholder/EmptyPlaceholder";
import MainLoader from "../../../components/loading/MainLoader";
import ProductAndCircuitIdSelector from "../../../components/productAndCircuitIdSelector/ProductAndCircuitIdSelector";
import SelectOption, {
  ISelectOption,
} from "../../../components/selectOption/SelectOption";
import StatusAlertModel from "../../../components/statusAlertModel/StatusAlertModel";
import Title from "../../../components/title/Title";
import { eligibleExtraGbForPackages } from "../../../const/eligibleExtraGbForPackages";
import { ICustomerCircuit } from "../../../interfaces/customerCircuid";
import { IExtraGbPackage } from "../../../interfaces/extraGbPackage";
import { EPackageType } from "../../../interfaces/packageType";
import { PROFILE_ACTIONS } from "../../../interfaces/profileData";
import { ISuccessState } from "../../../interfaces/successState";
import {
  getExtraGbPackage,
  purchaseExtraGBPostpaid,
} from "../../../services/dashboard";
import { IRootState } from "../../../store/configureStore";
import { getDataWithCheckingActionPrivilege } from "../../../util/hasActionPrivileges";
import styles from "./AddExtraData.module.scss";

const AddExtraData = () => {
  const [selectedPackageType, setSelectedPackageType] = useState<
    EPackageType | []
  >([]);
  const [selectedCircuitId, setSelectedCircuitId] = useState<
    ISelectOption | []
  >([]);
  const [dataAmounts, setDataAmounts] = useState<ISelectOption[] | []>([]);
  const [selectedDataAmount, setSelectedDataAmount] = useState<
    ISelectOption | []
  >([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [broadbandId, setBroadbandId] = useState<string>();

  const { circuits } = useSelector(({ dashboard }: IRootState) => ({
    circuits: dashboard.circuits,
  }));

  const getEligibleGbAmount = (
    selectedCircuit: ICustomerCircuit
  ): number[] | undefined => {
    return eligibleExtraGbForPackages.find(
      (gbPackage) => gbPackage.packageName === selectedCircuit.packageName
    )?.eligibleAmount;
  };

  const handleChangeCircuitId = (value: ISelectOption | []) => {
    setSelectedCircuitId(value);
    setDataAmounts([]);
    setSelectedDataAmount([]);
    setBroadbandId("");

    const selectedCircuit: ICustomerCircuit | undefined = circuits.find(
      (circuit) => circuit.circuitId === (value as ISelectOption).value
    );

    if (!selectedCircuit) {
      return;
    }

    // if (selectedPackageType == EPackageType.VBIL){
    //   setDataAmounts([{label: '1 TB', value: 1024}])
    // }else {
    //   const eligibleGB = getEligibleGbAmount(selectedCircuit);

    //   if (!eligibleGB) {
    //     return;
    //   }

    //   setDataAmounts(
    //       eligibleGB.map((gb) => ({
    //         label: gb + ' GB',
    //         value: gb,
    //       }))
    //   );
    // }

    setIsLoading(true);
    getDataWithCheckingActionPrivilege(
      true,
      selectedCircuit.circuitId,
      PROFILE_ACTIONS.EXTRA_GB,
      () => getExtraGbPackage(selectedCircuit.circuitId)
    )
      .then((packages: IExtraGbPackage[]) => {
        setDataAmounts(
          packages.map((pkg: IExtraGbPackage) => ({
            label: pkg.volume + " " + pkg.volumeUnit,
            value: pkg.volumeUnit,
            data: pkg,
          }))
        );
        setIsLoading(false);
      })
      .catch((error) => {
        setIsSuccess({
          isSuccess: false,
          message: error.message,
        });
        setIsLoading(false);
      });
  };

  const onSearchInBroadbandIdHandler = (id: string) => {
    setBroadbandId(id);
    setDataAmounts([]);
    setSelectedDataAmount([]);

    if (!id) {
      return;
    }

    setIsLoading(true);
    getDataWithCheckingActionPrivilege(true, id, PROFILE_ACTIONS.EXTRA_GB, () =>
      getExtraGbPackage(id)
    )
      .then((packages: IExtraGbPackage[]) => {
        setDataAmounts(
          packages.map((pkg: IExtraGbPackage) => ({
            label: pkg.volume + " " + pkg.volumeUnit,
            value: pkg.volumeUnit,
            data: pkg,
          }))
        );
        setIsLoading(false);
      })
      .catch((error) => {
        setIsSuccess({
          isSuccess: false,
          message: error.message,
        });
        setIsLoading(false);
      });
  };

  const handleCancelAddExtraData = () => {
    setSelectedCircuitId([]);
    setDataAmounts([]);
    setSelectedDataAmount([]);
  };

  const handleShowConfirmation = () => {
    if (!getEnteredCircuitOrBroadbandId()) {
      setIsSuccess({
        isSuccess: false,
        message: "Please select a circuit id or enter broadband id",
      });
      return;
    }

    if (!(selectedDataAmount as ISelectOption).value) {
      setIsSuccess({
        isSuccess: false,
        message: "Please select extra data amount.",
      });
      return;
    }

    setShowConfirmation(true);
  };

  const getChannelByPackageType = (
    packageType: EPackageType | []
  ): string | undefined => {
    switch (packageType) {
      case EPackageType.VBIL:
        return "VOB";
      case EPackageType.ENET:
        return "ENT";
      case EPackageType.BROADBAND:
        return "SCP";
      default:
        return undefined;
    }
  };

  const handleAddExtraData = () => {
    const broadbandOrCircuitId = getEnteredCircuitOrBroadbandId();
    const vodAmount = (selectedDataAmount as ISelectOption).value;
    const channel = getChannelByPackageType(selectedPackageType);

    if (!broadbandOrCircuitId || !vodAmount) {
      return;
    }

    if (!channel) {
      setIsSuccess({
        isSuccess: false,
        message: "Please select a product before adding extra data.",
      });
      return;
    }

    setIsLoading(true);
    setShowConfirmation(false);

    let purchaseFn = () =>
      purchaseExtraGBPostpaid(
        getEnteredCircuitOrBroadbandId(),
        getSelectedDataAmountPackage().packageId,
        channel
      );

    purchaseFn()
      .then(() => {
        setIsSuccess({
          isSuccess: true,
          message: "Extra GB added successfully!",
        });
        setIsLoading(false);
        setSelectedDataAmount([]);
      })
      .catch((err) => {
        setIsSuccess({
          isSuccess: false,
          message: err.message,
        });
        setIsLoading(false);
      });
  };

  const getEnteredCircuitOrBroadbandId = (): string => {
    return selectedPackageType === EPackageType.BROADBAND
      ? broadbandId
      : (selectedCircuitId as ISelectOption).value;
  };

  const getSelectedDataAmountPackage = (): IExtraGbPackage => {
    return (selectedDataAmount as ISelectOption)?.data as IExtraGbPackage;
  };

  return (
    <div className="page-container">
      <MainLoader show={isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/serviceLineUp", title: "Service Line-up" },
          { path: "", title: "Add Extra GB/Data" },
        ]}
      />

      <Title>Add Extra GB/Data</Title>

      <ProductAndCircuitIdSelector
        selectedCircuitId={selectedCircuitId}
        selectedPackageType={selectedPackageType}
        setSelectedPackageType={setSelectedPackageType}
        handleChangeCircuitId={handleChangeCircuitId}
        className="mt-1"
        onSearchInBroadbandId={onSearchInBroadbandIdHandler}
      />

      {getEnteredCircuitOrBroadbandId() ? (
        <Row className="page-sub-container-v2 mt-4">
          <Col>
            {dataAmounts.length > 0 ? (
              <>
                <Row>
                  <Col
                    className={[
                      "col-12 col-md-6 mb-2 mb-md-0",
                      styles.description,
                    ].join(" ")}
                  >
                    <>
                      Please select the Extra Data amount to be added for{" "}
                      {selectedPackageType === EPackageType.BROADBAND
                        ? "broadband account"
                        : "circuit ID"}
                      :{" "}
                      <span className="fw-bold">
                        {getEnteredCircuitOrBroadbandId()}
                      </span>
                    </>
                  </Col>
                  <Col className="col-12 col-md-6">
                    <SelectOption
                      placeholder="Please Select"
                      options={dataAmounts as ISelectOption[]}
                      onChange={(value) => setSelectedDataAmount(value)}
                      value={selectedDataAmount}
                    />
                  </Col>
                </Row>
                {selectedPackageType === EPackageType.BROADBAND &&
                  getSelectedDataAmountPackage() && (
                    <Row>
                      <Col className="text-center mt-4">
                        <Title>
                          {`Rs. ${(+getSelectedDataAmountPackage()
                            .postPrice).toFixed(2)} + Tax`}
                        </Title>
                      </Col>
                    </Row>
                  )}
                <Row className="justify-content-center mt-5">
                  <Col className="col-md-4 mb-2 mb-md-0">
                    <Button
                      type="submit"
                      className="w-100"
                      variant="success"
                      onClick={handleShowConfirmation}
                    >
                      Add to Bill
                    </Button>
                  </Col>
                  <Col className="col-md-4">
                    <Button
                      type="submit"
                      className="w-100"
                      variant="light"
                      onClick={handleCancelAddExtraData}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </>
            ) : (
              <EmptyMessage
                show
                message={`${selectedPackageType}: ${getEnteredCircuitOrBroadbandId()} not entitle for this service`}
              />
            )}
          </Col>
        </Row>
      ) : (
        <EmptyPlaceholder className="mt-5" />
      )}

      {/* Add data confirmation */}
      <AlertModal
        show={showConfirmation}
        variant="success"
        buttons={[
          {
            text: "Add to Bill",
            onClick: handleAddExtraData,
            variant: "success",
          },
          {
            text: "NO",
            onClick: () => setShowConfirmation(false),
            variant: "danger",
          },
        ]}
      >
        Do you want to add extra GB/Data for{" "}
        {selectedPackageType === EPackageType.BROADBAND
          ? "broadband account"
          : "circuit ID"}
        : {getEnteredCircuitOrBroadbandId()}
      </AlertModal>

      <StatusAlertModel isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
    </div>
  );
};

export default AddExtraData;
